/**
 * A HOME'S REGULAR CLEANS IN WORDS, as Account's "Your regular cleans" says them (slice A1), and as
 * each sheet of her asks about them (round five, slice C3) and Your agreement's "Ask for a change"
 * say them under the home's name: its cadence and weekday, and a pause that has not ended; and
 * whether an ending of them asked now cancels rather than ends (`cancelOf`), on its button, and the
 * one clean such a cancel names, which its sheet sends the day before of (`cancelVisitOf`). The line
 * that says what was asked reads it from the ask itself (`CleansAskShown`'s `cancel`, as the Worker
 * read it when she asked), never from here: once ops ends the agreement as she asked, it is no
 * longer live, and a reading of it now would say the same cancel as an ending (the review of the
 * fifth client-journey walk's leftovers, 2026-10-20).
 */
import type { CleansAskType, HomeCleans, RegularCleans } from '../api.ts';
import { dateInWords, dayInWords, daysAfter } from './dates.ts';
import {
  ACCOUNT,
  AGREEMENT,
  cadenceLine,
  cancelAsk,
  cancelVisitAsked,
  CLEANS,
  pausedFrom,
  regularly,
  visitKindWords,
} from '../words.ts';

/**
 * A home's pause in words, as Account, the sheets and Schedule say it: "Paused from 1 November until
 * 15 March.", or "Paused from 1 November." with no end from ops; "Paused." alone, Your agreement's
 * word for a paused agreement with no days, where ops holds the schedule paused with no days left
 * to say (`paused`, P1); null for none, or a first day that is not real.
 */
export function pauseSaid(home: RegularCleans): string | null {
  const from = home.pausedFrom === undefined ? null : dateInWords(home.pausedFrom);
  if (from !== null) {
    const until = home.pausedUntil === undefined ? null : dateInWords(home.pausedUntil);
    return pausedFrom(from, until);
  }
  return home.paused === true && home.pausedFrom === undefined ? AGREEMENT.paused : null;
}

/**
 * Whether ops holds a home's regular cleans paused (`paused`, P1), or the Worker gave a pause's
 * first day: what keeps a new cadence off the change sheet, which ops refuses on a paused schedule.
 */
export const pausedHome = (home: RegularCleans): boolean =>
  home.paused === true || home.pausedFrom !== undefined;

/**
 * A home's regular cleans in words: its cadence and weekday, and a pause that has not ended
 * (`pauseSaid`); "No regular cleans." for none or a one-time one; and nothing for a cadence or a
 * weekday this build has no word for, rather than a wrong one. Never the window: two times on a page
 * about the standing schedule would read as how long a clean lasts.
 */
export function cleansLine(home: RegularCleans): string | null {
  if (!repeatsListed(home)) return ACCOUNT.noRegularCleans;
  const line = cadenceLine(home.cadence, home.weekday);
  if (line === null) return null;
  const pause = pauseSaid(home);
  return pause === null ? regularly(line) : `${regularly(line)} ${pause}`;
}

/**
 * Whether a home's regular cleans come again, as its line lists them: a cadence, and not
 * `one_time`. Where they do not, its line is "No regular cleans." (`cleansLine`).
 */
export const repeatsListed = (home: RegularCleans): boolean =>
  home.cadence !== undefined && home.cadence !== 'one_time';

/**
 * WHAT AN ENDING CANCELS (the fifth client-journey walk, 2026-10-20, where a home whose only live
 * agreement was a one-time clean drew "No regular cleans." and then "Ask to end"): every service an
 * ending of the home's may name, where none of them comes again (the Worker's `oneTime`) and no
 * repeating clean is listed for the home, so the ask is said as a cancel of them; null where the
 * ending ends something that comes again, which is said as an ending. The ask is the same either
 * way, `service.end`, which ops takes. The Worker's request door reads the same when she asks
 * (`cancelledAt` in `api/src/seam/views.ts`) and keeps it on the ask, which is what its line says.
 */
export function cancelOf(home: HomeCleans): readonly string[] | null {
  if (repeatsListed(home) || home.services.length === 0) return null;
  const once = home.oneTime ?? [];
  return home.services.every((service) => once.includes(service)) ? home.services : null;
}

/** A button's words, by the ask it opens, and its sheet's name. */
const OPENS: Readonly<Record<CleansAskType, string>> = {
  'schedule.pause': CLEANS.pause,
  'schedule.resume': CLEANS.resume,
  'schedule.change': CLEANS.change,
  'service.end': CLEANS.end,
};

/**
 * The words of the button that opens an ask about a home, which name its sheet too
 * (`components/CleansAsks.tsx`, `CleansSheet.tsx`): an ending that ends nothing that repeats says
 * it cancels what it names (`cancelOf`, `cancelAsk`), each service by its open visit's kind and day
 * where the Worker gives one (`cancelVisits`, ops's answer on ops #76, 2026-10-22), "Ask to cancel
 * the deep clean on Tuesday 27 October", and by its name alone where it gives none; and every other
 * ask is as `OPENS` says it.
 */
export function askWords(type: CleansAskType, home: HomeCleans): string {
  const cancels = type === 'service.end' ? cancelOf(home) : null;
  return cancels === null ? OPENS[type] : cancelAsk(cancels, visitsNamed(home));
}

/** A visit a cancel names: ops's kind, its day in words and its day, `YYYY-MM-DD`. */
interface VisitNamed {
  readonly kind: string;
  readonly day: string;
  readonly date: string;
}

/**
 * Each service's open visit the Worker gives, by its kind and its day in words; none whose day is
 * not real, or whose kind this build has no words for, which the button names by its service alone.
 */
function visitsNamed(home: HomeCleans): Map<string, VisitNamed> {
  const named = new Map<string, VisitNamed>();
  for (const visit of home.cancelVisits ?? []) {
    const day = dayInWords(visit.date);
    if (day !== null && visitKindWords(visit.kind) !== null && !named.has(visit.service)) {
      named.set(visit.service, { kind: visit.kind, day, date: visit.date });
    }
  }
  return named;
}

/**
 * THE CLEAN A CANCEL OF A SERVICE CANCELS (the review of 2621dcd), where the button names one
 * (`askWords`): the line that says it on the sheet (`cancelVisitAsked`), and the day before it,
 * which the sheet sends as ops's `last_day` in place of a day she chooses, so the office ends the
 * agreement before that clean. The Worker names a visit only where it is the one open one-time
 * visit of the service at the home after today, so the ask cancels it and nothing else. Null where
 * none is named, or where the ending is no cancel, and the sheet asks the day as before.
 */
export function cancelVisitOf(
  home: HomeCleans,
  service: string,
): { readonly said: string; readonly lastDay: string } | null {
  if (cancelOf(home)?.includes(service) !== true) return null;
  const visit = visitsNamed(home).get(service);
  if (visit === undefined) return null;
  const said = cancelVisitAsked(visit.kind, visit.day);
  const lastDay = daysAfter(visit.date, -1);
  return said === null || lastDay === null ? null : { said, lastDay };
}
